import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  accentThemes,
  colorModes,
  langs,
  type AccentTheme,
  type ColorMode,
  type Lang,
} from '@seenaly/shared';
import { dictFor, localeFor, type Dict } from '@/i18n';

/**
 * Language, accent theme and light/dark mode.
 *
 * The values live on <html> as data-cc-* attributes, which is what tokens.css
 * and fonts.css key off. index.html applies the stored choice before React
 * mounts, so this provider re-reads the same keys rather than defining its own
 * defaults — otherwise the first render would fight the pre-paint script and
 * the theme would flash (docs/spec.md §8.2).
 *
 * From P8 these also persist server-side in `user_prefs`; localStorage stays as
 * the fast path so a reload never waits on the network to pick a theme.
 */

const STORAGE_KEYS = {
  lang: 'sn.lang',
  theme: 'sn.theme',
  mode: 'sn.mode',
} as const;

interface Prefs {
  lang: Lang;
  theme: AccentTheme;
  mode: ColorMode;
  t: Dict;
  locale: string;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  setTheme: (theme: AccentTheme) => void;
  setMode: (mode: ColorMode) => void;
  toggleMode: () => void;
}

const PrefsContext = createContext<Prefs | null>(null);

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    return allowed.includes(stored as T) ? (stored as T) : fallback;
  } catch {
    // Private browsing and locked-down kiosk profiles can throw on access.
    return fallback;
  }
}

function persist(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Non-fatal: the choice simply will not survive a reload.
  }
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readStored(STORAGE_KEYS.lang, langs, 'la'));
  const [theme, setThemeState] = useState<AccentTheme>(() =>
    readStored(STORAGE_KEYS.theme, accentThemes, 'rose'),
  );
  const [mode, setModeState] = useState<ColorMode>(() =>
    readStored(STORAGE_KEYS.mode, colorModes, 'light'),
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-cc-lang', lang);
    // `lang` also drives hyphenation and screen-reader pronunciation, so the
    // real BCP 47 tag goes on the element too.
    root.setAttribute('lang', lang === 'la' ? 'lo' : 'en');
    persist(STORAGE_KEYS.lang, lang);
  }, [lang]);

  useEffect(() => {
    document.documentElement.setAttribute('data-cc-theme', theme);
    persist(STORAGE_KEYS.theme, theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-cc-mode', mode);
    persist(STORAGE_KEYS.mode, mode);
  }, [mode]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);
  const setTheme = useCallback((next: AccentTheme) => setThemeState(next), []);
  const setMode = useCallback((next: ColorMode) => setModeState(next), []);
  const toggleLang = useCallback(() => setLangState((cur) => (cur === 'la' ? 'en' : 'la')), []);
  const toggleMode = useCallback(
    () => setModeState((cur) => (cur === 'light' ? 'dark' : 'light')),
    [],
  );

  const value = useMemo<Prefs>(
    () => ({
      lang,
      theme,
      mode,
      t: dictFor(lang),
      locale: localeFor[lang],
      setLang,
      toggleLang,
      setTheme,
      setMode,
      toggleMode,
    }),
    [lang, theme, mode, setLang, toggleLang, setTheme, setMode, toggleMode],
  );

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): Prefs {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs must be used inside <PrefsProvider>');
  return ctx;
}

/** Shorthand for the common case of only needing the dictionary. */
export function useT(): Dict {
  return usePrefs().t;
}
